<template>
    <!-- Grouped Touchpoints Layout -->
    <div v-if="groupedTouchpoints" class="grouped-touchpoints mt-4">
        <div v-for="group in groupedTouchpoints" :key="group.key" class="touchpoint-group mb-4">
            <h4 class="mb-3">{{ group.label }} ({{ group.touchpoints.length }})</h4>
            <div class="touchpoints-grid-layout">
                <touchpoint-content-card
                    v-for="touchpoint in group.touchpoints"
                    :key="touchpoint.id"
                    :touchpoint="touchpoint"
                    :is-collapsed="collapsedCards.includes(touchpoint.id)"
                    :is-selected="selectedCards.includes(touchpoint.id)"
                    :show-mini-menu="miniMenuOpenFor === touchpoint.id"
                    :is-primary-asset="false"
                    :show-primary-card="true"
                    :show-primary-inputs="true"
                    :touchpoint-status="getTouchpointStatus(touchpoint)"
                    :is-draft="isDraftTouchpoint(touchpoint)"
                    :mini-menu-two-column="true"
                    @toggle="toggleTouchpoint"
                    @view-asset-type-info="viewAssetTypeInfo"
                    @open-mini-menu="openMiniMenu"
                    @close-mini-menu="closeMiniMenu"
                    @edit="editTouchpoint"
                    @approve="approveTouchpoint"
                    @deploy="deployTouchpoint"
                    @duplicate="duplicateTouchpoint"
                    @review="reviewTouchpoint"
                    @delete="deleteTouchpoint"
                    @card-click="toggleSelection"
                />
            </div>
        </div>
    </div>

    <!-- Ungrouped Touchpoints Grid Layout -->
    <div v-else class="touchpoints-grid-layout">
        <!-- All Touchpoints -->
        <touchpoint-content-card
            v-for="touchpoint in touchpoints"
            :key="touchpoint.id"
            :touchpoint="touchpoint"
            :is-collapsed="collapsedCards.includes(touchpoint.id)"
            :is-selected="selectedCards.includes(touchpoint.id)"
            :show-mini-menu="miniMenuOpenFor === touchpoint.id"
            :is-primary-asset="isPrimaryAsset(touchpoint)"
            :show-primary-card="true"
            :show-primary-inputs="true"
            :touchpoint-status="getTouchpointStatus(touchpoint)"
            :is-draft="isDraftTouchpoint(touchpoint)"
            :mini-menu-two-column="!isPrimaryAsset(touchpoint)"
            @toggle="toggleTouchpoint"
            @view-asset-type-info="viewAssetTypeInfo"
            @open-mini-menu="openMiniMenu"
            @close-mini-menu="closeMiniMenu"
            @edit="editTouchpoint"
            @approve="approveTouchpoint"
            @deploy="deployTouchpoint"
            @duplicate="duplicateTouchpoint"
            @review="reviewTouchpoint"
            @delete="deleteTouchpoint"
            @card-click="toggleSelection"
        />
    </div>
    
    <!-- Edit Touchpoint Modal Component -->
    <edit-touchpoint-modal 
        :asset="selectedAssetForModal" 
        :status="selectedAssetStatus"
        :show="showModal" 
        :concepts="concepts"
        :client="client"
        @edit-asset="handleEditAsset"
        @close="closeModal" />

    <asset-type-info-modal :asset-type="selectedAssetType" :show="showAssetTypeModal" @close="closeAssetTypeModal" />
</template>

<script>
import permissionsMixin from '../../mixins/permissions';
import filtersMixin from '../../mixins/filters';
import { usePlaybookStore } from '../../stores/playbook';
import { useClientStore } from '../../stores/client';
import { useNotifications } from '../../composables/useNotifications';
import AssetTypeInfoModal from './AssetTypeInfoModal.vue';
import EditTouchpointModal from './EditTouchpointModal.vue';
import TouchpointContentCard from './TouchpointContentCard.vue';

export default {
    name: 'TouchpointsGridView',
    mixins: [permissionsMixin, filtersMixin],
    components: { AssetTypeInfoModal, EditTouchpointModal, TouchpointContentCard },
    emits: ['cards-state-changed', 'edit-asset', 'review-single-touchpoint'],
    props: {
        touchpoints: {
            type: Array,
            default: () => []
        },
        concepts: {
            type: Array,
            default: () => []
        },
        selectedAssetIds: {
            type: Array,
            default: () => []
        },
        hidePrimaryStyling: {
            type: Boolean,
            default: false
        },
        groupBy: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            selectedCards: [],
            collapsedCards: [],
            miniMenuOpenFor: null,
            selectedAssetId: null,
            showModal: false,
            selectedAssetType: null,
            showAssetTypeModal: false,
        };
    },
    setup() {
        const playbookStore = usePlaybookStore();
        const clientStore = useClientStore();
        const { showNotification } = useNotifications();
        return {
            playbookStore,
            clientStore,
            showNotification
        };
    },

    mounted() {
        // Initialize selection from prop
        if (Array.isArray(this.selectedAssetIds) && this.selectedAssetIds.length > 0) {
            this.selectedCards = [...this.selectedAssetIds];
        }
        
        // Initialize tooltips
        const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
        const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new window.bootstrap.Tooltip(tooltipTriggerEl));

        // Add event listeners to hide tooltips on click and mouseleave
        for (var i = 0; i < tooltipTriggerList.length; i++) {
          (function(index) {
            tooltipTriggerList[index].addEventListener('click', function() {
              tooltipList[index].hide();
            });
            tooltipTriggerList[index].addEventListener('mouseleave', function() {
              tooltipList[index].hide();
            });
          })(i);
        }
    },
    computed: {
        client() {
            return this.clientStore.getClient;
        },
        playbook() {
            return this.playbookStore.getSelectedPlaybook;
        },
        primaryAsset() {
            // Find the primary asset from the current touchpoints (filtered or all)
            return this.touchpoints.find(asset => 
                asset.playbook_type_asset_type && asset.playbook_type_asset_type.is_primary
            ) || this.touchpoints[0];
        },
        // touchpoints is now a prop, so we don't need this computed property anymore
        allCardsCollapsed() {
            return this.touchpoints.length > 0 && 
                   this.touchpoints.every(tp => this.collapsedCards.includes(tp.id));
        },
        allCardsSelected() {
            return this.touchpoints.length > 0 && 
                   this.touchpoints.every(tp => this.selectedCards.includes(tp.id));
        },
        anyCardsSelected() {
            return this.selectedCards.length > 0;
        },
        selectedAssetForModal() {
            // Make this reactive to store updates by finding the asset from the store
            if (!this.selectedAssetId || !this.playbookStore.playbook_selected.assets) {
                return null;
            }
            return this.playbookStore.playbook_selected.assets.find(a => a.id === this.selectedAssetId) || null;
        },
        selectedAssetStatus() {
            if (!this.selectedAssetForModal) {
                return null;
            }
            return this.getTouchpointStatus(this.selectedAssetForModal);
        },
        groupedTouchpoints() {
            // If no grouping is selected, return null to indicate no grouping
            if (!this.groupBy) {
                return null;
            }

            // Group touchpoints by the selected option
            const groups = {};
            
            this.touchpoints.forEach(touchpoint => {
                let groupKey = '';
                let groupLabel = '';
                
                switch (this.groupBy) {
                    case 'type':
                        groupKey = touchpoint.asset_type?.id || 'no-type';
                        groupLabel = touchpoint.asset_type?.name || 'No Type';
                        break;
                    case 'launch_date':
                        let dateToUse = null;
                        if (touchpoint.launch_date) {
                            dateToUse = touchpoint.launch_date;
                        } else if (this.playbook?.start_date) {
                            // Use playbook start_date for touchpoints without launch_date (similar to calendar view)
                            dateToUse = this.playbook.start_date;
                        }
                        
                        if (dateToUse) {
                            const date = new Date(dateToUse);
                            groupKey = date.toISOString().split('T')[0]; // YYYY-MM-DD
                            groupLabel = this.formatDateForGroupLabel(dateToUse);
                        } else {
                            groupKey = 'no-date';
                            groupLabel = 'No Launch Date';
                        }
                        break;
                    case 'audience':
                        groupKey = touchpoint.segment?.id || 'no-audience';
                        groupLabel = touchpoint.segment?.name || 'No audience attached';
                        break;
                    default:
                        return null;
                }
                
                if (!groups[groupKey]) {
                    groups[groupKey] = {
                        key: groupKey,
                        label: groupLabel,
                        touchpoints: []
                    };
                }
                
                groups[groupKey].touchpoints.push(touchpoint);
            });
            
            // Convert to array and sort
            return Object.values(groups).sort((a, b) => {
                // Sort by label alphabetically, except "No X" items go to the end
                if (a.label.startsWith('No ') && !b.label.startsWith('No ')) return 1;
                if (!a.label.startsWith('No ') && b.label.startsWith('No ')) return -1;
                if (this.groupBy === 'launch_date') {
                    // For dates, sort chronologically (newest first for "No date" at end)
                    if (a.key === 'no-date') return 1;
                    if (b.key === 'no-date') return -1;
                    return b.key.localeCompare(a.key); // Descending (newest first)
                }
                return a.label.localeCompare(b.label);
            });
        }
    },
    methods: {
        toggleAllCards() {
            if (this.allCardsCollapsed) {
                // Expand all
                this.collapsedCards = [];
            } else {
                // Collapse all
                this.collapsedCards = this.touchpoints.map(tp => tp.id);
            }
            this.$emit('cards-state-changed');
        },
        
        toggleSelection(touchpoint) {
            if (touchpoint.status == 'generating') {
                return;
            }
            const index = this.selectedCards.indexOf(touchpoint.id);
            if (index > -1) {
                this.selectedCards.splice(index, 1);
            } else {
                this.selectedCards.push(touchpoint.id);
            }
            this.$emit('cards-state-changed', [...this.selectedCards]);
        },
        
        toggleSelectAll() {
            if (this.anyCardsSelected) {
                // If any cards are selected, deselect all
                this.selectedCards = [];
            } else {
                // If no cards are selected, select all
                this.selectedCards = this.touchpoints.map(tp => tp.id);
            }
            this.$emit('cards-state-changed', [...this.selectedCards]);
        },
        
        async deleteSelected() {
            if (this.selectedCards.length > 0) {
                Swal.fire({
                    title: `Are you sure you want to delete ${this.selectedCards.length} touchpoint${this.selectedCards.length === 1 ? '' : 's'}?`,
                    text: "This action cannot be undone.",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonText: "Yes, delete",
                    customClass: {
                        popup: 'swal-warning'
                    },
                }).then(async (result) => {
                    if (result.isConfirmed) {
                        try {
                            const idsToDelete = [...this.selectedCards];
                            await Promise.all(idsToDelete.map(async (id) => {
                                const tp = this.touchpoints.find(t => t.id === id);
                                if (tp) {
                                    await this.performDeleteTouchpoint(tp, true);
                                }
                            }));

                            // Clear selection and notify
                            this.selectedCards = [];
                            this.$emit('cards-state-changed', [...this.selectedCards]);

                            const count = idsToDelete.length;
                            this.showNotification({
                                text: count === 1 
                                    ? '<strong>' + this.touchpoints.find(t => t.id === idsToDelete[0])?.name + '</strong> was successfully deleted!'
                                    : count + ' touchpoints were successfully deleted!'
                            });
                        } catch (error) {
                            console.error('Error deleting selected touchpoints:', error);
                            this.showNotification({
                                text: 'Failed to delete some touchpoints. Please try again.',
                                type: 'error'
                            });
                        }
                    }
                });
            }
        },
        
        editTouchpoint(touchpoint) {
            this.selectedAssetId = touchpoint.id;
            this.showModal = true;
            this.closeMiniMenu();
        },
        
        toggleTouchpoint(touchpoint) {
            const index = this.collapsedCards.indexOf(touchpoint.id);
            if (index > -1) {
                this.collapsedCards.splice(index, 1);
            } else {
                this.collapsedCards.push(touchpoint.id);
            }
            this.$emit('cards-state-changed', [...this.selectedCards]);
        },
        
        isPrimaryAsset(touchpoint) {
            // When grouped, hide primary styling so all touchpoints look the same
            if (this.hidePrimaryStyling) {
                return false;
            }
            return this.primaryAsset && this.primaryAsset.id === touchpoint.id;
        },

        viewAssetTypeInfo(touchpoint) {
            this.selectedAssetType = touchpoint.asset_type;
            this.showAssetTypeModal = true;
        },

        openMiniMenu(touchpoint) {
            if (this.miniMenuOpenFor === touchpoint.id) {
                this.miniMenuOpenFor = null;
            } else {
                this.miniMenuOpenFor = touchpoint.id;
            }
        },
        
        closeMiniMenu() {
            this.miniMenuOpenFor = null;
        },
        
        async approveTouchpoint(touchpoint) {
            try {
                this.closeMiniMenu();
                
                const response = await window.axios.post(`/api/playbooks/${this.client.url}/assets/${touchpoint.id}/approve`);
                
                if (response.data && response.data.asset) {
                    // Update the asset in the store
                    this.playbookStore.updateAsset(response.data.asset.id, response.data.asset);
                    
                    this.showNotification({
                        text: '<strong>' + response.data.asset.name + '</strong> was successfully updated!'
                    });
                }
            } catch (error) {
                console.error('Error approving touchpoint:', error);
                
                this.showNotification({
                    text: 'Failed to update <strong>' + touchpoint.name + '</strong>. Please try again.',
                    type: 'error'
                });
            }
        },
        
        deployTouchpoint(touchpoint) {
            this.closeMiniMenu();
            // Reuse review event to trigger launch flow upstream with this single touchpoint
            this.$emit('review-single-touchpoint', touchpoint);
        },
        
        async duplicateTouchpoint(touchpoint) {
            try {
                this.closeMiniMenu();
                
                // Create a copy of the touchpoint object
                const duplicatedAsset = {
                    ...touchpoint,
                    name: touchpoint.name + ' (Copy)',
                    id: null, // Will be set by backend
                    status: 'draft',
                    approved_by: null,
                    approved_at: null,
                    source: 'manual',
                    source_details: 'Duplicated from existing touchpoint'
                };
                
                // Optimistically add to store
                this.playbookStore.addAsset(duplicatedAsset);
                
                // Create the duplicate on the backend
                const response = await window.axios.post(`/api/playbooks/${this.client.url}/assets`, {
                    client_id: this.client.id,
                    playbook_id: touchpoint.playbook_id,
                    playbook_asset_type_id: touchpoint.playbook_asset_type_id,
                    name: duplicatedAsset.name,
                    description: touchpoint.description,
                    launch_date: touchpoint.launch_date,
                    inputs: touchpoint.inputs,
                    status: 'draft',
                    segment: touchpoint.segment,
                    destination_asset_id: touchpoint.destination_asset_id
                });
                
                if (response.data && response.data.asset) {
                    // Remove optimistic asset with null ID if it exists
                    if (this.playbookStore.playbook_selected.assets) {
                        const optimisticIndex = this.playbookStore.playbook_selected.assets.findIndex(a => a.id === null && a.name === duplicatedAsset.name);
                        if (optimisticIndex >= 0) {
                            this.playbookStore.playbook_selected.assets.splice(optimisticIndex, 1);
                        }
                    }
                    
                    // Add the actual asset data from backend
                    this.playbookStore.addAsset(response.data.asset);
                    
                    // Open the edit modal for the new asset
                    this.selectedAssetId = response.data.asset.id;
                    this.showModal = true;
                    
                    this.showNotification({
                        text: '<strong>' + response.data.asset.name + '</strong> was successfully created!'
                    });
                }
            } catch (error) {
                console.error('Error duplicating touchpoint:', error);
                
                // Remove the optimistically added asset on error (find by null ID and matching name)
                if (this.playbookStore.playbook_selected.assets) {
                    const optimisticIndex = this.playbookStore.playbook_selected.assets.findIndex(a => a.id === null && a.name === duplicatedAsset.name);
                    if (optimisticIndex >= 0) {
                        this.playbookStore.playbook_selected.assets.splice(optimisticIndex, 1);
                    }
                }
                
                this.showNotification({
                    text: 'Failed to duplicate <strong>' + touchpoint.name + '</strong>. Please try again.',
                    type: 'error'
                });
            }
        },
        
        reviewTouchpoint(touchpoint) {
            this.closeMiniMenu();
            
            // Emit event to parent to handle the review/export functionality
            this.$emit('review-single-touchpoint', touchpoint);
        },
        
        async deleteTouchpoint(touchpoint) {
            Swal.fire({
                title: `Are you sure you want to delete "${touchpoint.name}"?`,
                text: "This action cannot be undone.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, delete",
                customClass: {
                    popup: 'swal-warning'
                },
            }).then(async (result) => {
                if (result.isConfirmed) {
                    await this.performDeleteTouchpoint(touchpoint, false);
                } else {
                    this.closeMiniMenu();
                }
            });
        },

        async performDeleteTouchpoint(touchpoint, suppressToast = false) {
            try {
                // Call the delete endpoint
                await window.axios.delete(`/api/playbooks/${this.client.url}/assets/${touchpoint.id}`);

                // Remove from store
                this.playbookStore.removeAsset(touchpoint.id);

                // Ensure the deleted asset is also removed from selectedCards
                const selectedIndex = this.selectedCards.indexOf(touchpoint.id);
                if (selectedIndex > -1) {
                    this.selectedCards.splice(selectedIndex, 1);
                    this.$emit('cards-state-changed');
                }

                // Close mini menu
                this.closeMiniMenu();

                if (!suppressToast) {
                    this.showNotification({
                        text: '<strong>' + touchpoint.name + '</strong> was successfully deleted!'
                    });
                }
            } catch (error) {
                console.error('Error deleting touchpoint:', error);
                
                this.showNotification({
                    text: 'Failed to delete <strong>' + touchpoint.name + '</strong>. Please try again.',
                    type: 'error'
                });
                throw error;
            }
        },

        async handleEditAsset(asset) {
            try {
                // Use the store method to update the asset
                const result = await this.playbookStore.updateAssetFromAPI(asset);

                if (result.success) {
                    this.$nextTick(()=>{
                        this.showNotification({
                            text: '<strong>' + asset.name + '</strong> was successfully updated!'
                        });
                    });
                }
            
                this.closeModal();
                
            } catch (error) {
                console.error('Error updating asset:', error);
                
                this.showNotification({
                    text: 'Failed to update <strong>' + asset.name + '</strong>. Please try again.',
                    type: 'error'
                });
            }
        },

        closeModal() {
            this.showModal = false;
            this.selectedAssetId = null;
        },

        closeAssetTypeModal() {
            this.showAssetTypeModal = false;
        },

        getPromptLabel(placeholder, assetType) {
            // Get the title from asset_type object, or use placeholder as fallback
            if (assetType && assetType.input_types) {
                const inputType = assetType.input_types.find(type => type.name === placeholder);
                if (inputType && inputType.title) {
                    return inputType.title;
                }
            }
            // Fallback to formatted placeholder
            return placeholder.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        },

        formatPromptValue(value, placeholder) {
            // If it's a "link" type, return blank
            if (placeholder === 'link') {
                return '';
            }

            // If value is null or undefined, return empty string
            if (!value) {
                return '';
            }

            // Handle arrays - join with line breaks for visual separation
            if (Array.isArray(value)) {
                // Filter out empty values and join with line breaks
                const nonEmptyValues = value.filter(item => item !== null && item !== undefined && item !== '');
                if (nonEmptyValues.length === 0) {
                    return '';
                }
                
                // Format each value and join with double line breaks for clear separation
                const formattedValues = nonEmptyValues.map(item => {
                    let formattedItem = String(item);
                    // Replace h1-h6 tags with bold tags
                    formattedItem = formattedItem.replace(/<h[1-6][^>]*>/gi, '<strong>');
                    formattedItem = formattedItem.replace(/<\/h[1-6]>/gi, '</strong>');
                    return formattedItem;
                });
                
                // Join all values with double line breaks to create visual separation
                let formattedValue = formattedValues.join('<br><br>');
                
                // Count total lines across all values (including line breaks within each value)
                const totalLines = formattedValue.split(/<br\s*\/?>/i).length;
                if (totalLines > 4) {
                    // Split by <br> tags and limit to 4 lines
                    const parts = formattedValue.split(/<br\s*\/?>/i);
                    formattedValue = parts.slice(0, 4).join('<br>') + '...';
                }
                
                return formattedValue;
            }

            // Handle single values - convert to string and limit to 4 lines
            let formattedValue = String(value);
            
            // Replace h1-h6 tags with bold tags
            formattedValue = formattedValue.replace(/<h[1-6][^>]*>/gi, '<strong>');
            formattedValue = formattedValue.replace(/<\/h[1-6]>/gi, '</strong>');
            
            // Convert newlines to <br> tags for proper HTML rendering
            formattedValue = formattedValue.replace(/\n/g, '<br>');
            
            // Split into lines and limit to 4
            const lines = formattedValue.split(/<br\s*\/?>/i);
            if (lines.length > 4) {
                formattedValue = lines.slice(0, 4).join('<br>') + '...';
            }
            
            return formattedValue;
        },

        getTouchpointStatus(touchpoint) {
            // Returns an object with label and CSS classes for the status badge
            if (touchpoint.source == null || this.isDraftTouchpoint(touchpoint)) {
                return { label: 'Incomplete', classes: 'bg-red bg-icon-warning' };
            } else if (touchpoint.deployed_at) {
                return { label: 'Launched', classes: 'bg-orange bg-icon-launched' };
            } else if (touchpoint.status == 'approved') {
                return { label: 'Approved', classes: 'bg-green bg-icon-ready' };
            } else if (touchpoint.status == 'generating') {
                return { label: 'Generating', classes: 'bg-blue bg-icon-generating' };
            } else {
                return { label: 'Ready', classes: 'bg-green bg-icon-ready' };
            }
        },

        isDraftTouchpoint(touchpoint) {
            // A touchpoint is in draft mode if it's missing any required field
            if (!touchpoint || !touchpoint.asset_type || !touchpoint.asset_type.inputs || !touchpoint.inputs) {
                return true;
            }

            // Get required inputs (inputs where optional is false or not set)
            const requiredInputs = touchpoint.asset_type.inputs.filter(input => !input.optional);

            // Helper function to check if a value is empty
            const isEmpty = (value) => {
                if (value === undefined || value === null) return true;
                if (typeof value === 'string') return value.trim() === '';
                if (Array.isArray(value)) {
                    // Empty array is empty
                    if (value.length === 0) return true;
                    // Array with only null/undefined/empty values is considered empty
                    return value.every(item => {
                        if (item === null || item === undefined) return true;
                        if (typeof item === 'string') return item.trim() === '';
                        if (Array.isArray(item)) return isEmpty(item);
                        // For objects, check if they're empty (no meaningful content)
                        if (typeof item === 'object') {
                            // If it's an object, check if it has any non-empty properties
                            return Object.keys(item).length === 0 || 
                                   Object.values(item).every(val => isEmpty(val));
                        }
                        return false;
                    });
                }
                return false;
            };

            // Helper function to check group inputs
            const checkGroupInputs = (groupInput, groupItems) => {
                if (!groupItems || !Array.isArray(groupItems)) return true;

                // Get all required inputs for this group
                const groupRequiredInputs = touchpoint.asset_type.inputs.filter(
                    input => input.group_id === groupInput.id && !input.optional
                );

                // Check each item in the group
                for (const item of groupItems) {
                    // Skip null or undefined items
                    if (!item) continue;

                    for (const requiredInput of groupRequiredInputs) {
                        // Skip if requiredInput is null or doesn't have a placeholder
                        if (!requiredInput || !requiredInput.placeholder) continue;

                        // Safely access the value using the placeholder
                        const value = item[requiredInput.placeholder];
                        if (isEmpty(value)) {
                            return true; // Missing required field in group
                        }
                    }
                }

                return false; // All required fields in group are present
            };

            // Check each required input
            for (const requiredInput of requiredInputs) {
                // Skip if requiredInput is null or doesn't have a placeholder
                if (!requiredInput || !requiredInput.placeholder) continue;

                // Skip inputs that belong to a group (they're checked within their group)
                if (requiredInput.group_id) {
                    continue;
                }

                // Find the corresponding input value in touchpoint.inputs
                const input = Array.isArray(touchpoint.inputs) 
                    ? touchpoint.inputs.find(input => input && input.placeholder === requiredInput.placeholder)
                    : null;

                // If input is missing, it's a draft
                if (!input) {
                    // Exclude citations and link placeholders from required check
                    if (requiredInput.placeholder !== 'citations' && requiredInput.placeholder !== 'link') {
                        return true;
                    }
                    continue;
                }

                // Handle group inputs
                if (requiredInput.type === 'group') {
                    if (isEmpty(input.value) || checkGroupInputs(requiredInput, input.value)) {
                        return true; // Missing required group or required fields within group
                    }
                }
                // Handle regular inputs (not in a group)
                else if (isEmpty(input.value)) {
                    // Exclude citations and link placeholders from required check
                    if (requiredInput.placeholder !== 'citations' && requiredInput.placeholder !== 'link') {
                        return true; // Missing required field
                    }
                }
            }

            // All required fields are present
            return false;
        },

        // Format date to match DatepickerUtc display format (M/d/yyyy)
        formatDateForDisplay(dateValue) {
            if (!dateValue) return '';
            
            // Parse the date (handles both Date objects and ISO strings)
            const date = dateValue instanceof Date ? new Date(dateValue) : new Date(dateValue);
            
            // Adjust for timezone offset to preserve the date value (same as DatepickerUtc)
            // This ensures UTC dates like "2025-11-03T00:00:00.000000Z" display as 11/3/2025
            // regardless of the user's timezone
            const offset = date.getTimezoneOffset();
            date.setMinutes(date.getMinutes() + offset);
            
            // Format as M/d/yyyy to match DatepickerUtc's inputFormat (no leading zeros)
            const month = date.getMonth() + 1;
            const day = date.getDate();
            const year = date.getFullYear();
            
            return `${month}/${day}/${year}`;
        },

        // Format date for group labels: "Day, Full Date" (e.g., "Monday, November 3, 2025")
        formatDateForGroupLabel(dateValue) {
            if (!dateValue) return '';
            
            // Parse the date (handles both Date objects and ISO strings)
            const date = dateValue instanceof Date ? new Date(dateValue) : new Date(dateValue);
            
            // Adjust for timezone offset to preserve the date value
            const offset = date.getTimezoneOffset();
            date.setMinutes(date.getMinutes() + offset);
            
            // Format day name
            const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            const dayName = dayNames[date.getDay()];
            
            // Format month name
            const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                              'July', 'August', 'September', 'October', 'November', 'December'];
            const monthName = monthNames[date.getMonth()];
            
            // Get day and year
            const day = date.getDate();
            const year = date.getFullYear();
            
            return `${dayName}, ${monthName} ${day}, ${year}`;
        }
    },
    
    watch: {
        selectedAssetIds: {
            handler(newIds) {
                // Sync the prop with internal state
                this.selectedCards = Array.isArray(newIds) ? [...newIds] : [];
            },
            immediate: true
        }
    }

}
</script>